import { useEffect } from "react";
import { C } from "../lib/theme.js";
import ManageProperties from "./ManageProperties.jsx";
import ManageScoring from "./ManageScoring.jsx";
import ManageUploads from "./ManageUploads.jsx";

const TABS = [
  { id: "properties", label: "Properties", hint: "Aliases, units, owners, ownership %" },
  { id: "scoring", label: "Scoring", hint: "Benchmarks, weights, comparison basis" },
  { id: "uploads", label: "Uploads", hint: "Ingest new .xlsx, upload history" },
];

// Full-screen overlay with left-rail tab navigation. Callers control open/close
// and provide the current scoring/ownership state plus setters so Manage can
// write back directly to the parent without re-fetching on close.
export default function ManageModal({
  open,
  tab,
  onChangeTab,
  onClose,
  properties,
  ttmAvailable,
  basis,
  setBasis,
  benchmarks,
  setBenchmarks,
  weights,
  setWeights,
  ownerPcts,
  setOwnerPcts,
  onSaveSettings,
  onUploaded,
  onPropertiesSaved,
  saving,
  saveStatus,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={overlayStyle} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={shellStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: C.accent, fontFamily: "'DM Mono',monospace" }}>
              Manage
            </div>
            <div style={{ fontSize: 22, fontFamily: "'Instrument Serif',serif", color: C.text, lineHeight: 1.1 }}>
              {TABS.find((t) => t.id === tab)?.label || "Manage"}
            </div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>
              {TABS.find((t) => t.id === tab)?.hint}
            </div>
          </div>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={bodyStyle}>
          <nav style={railStyle}>
            {TABS.map((t) => {
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  onClick={() => onChangeTab(t.id)}
                  style={{
                    ...railBtnStyle,
                    background: active ? C.accentLight : "transparent",
                    color: active ? C.accent : C.sub,
                    borderLeft: active ? `3px solid ${C.accent}` : "3px solid transparent",
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </nav>

          <div style={paneStyle}>
            {tab === "properties" && (
              <ManageProperties
                ownerPcts={ownerPcts}
                setOwnerPcts={setOwnerPcts}
                onSaved={onPropertiesSaved}
              />
            )}
            {tab === "scoring" && (
              <ManageScoring
                properties={properties}
                ttmAvailable={ttmAvailable}
                basis={basis}
                setBasis={setBasis}
                benchmarks={benchmarks}
                setBenchmarks={setBenchmarks}
                weights={weights}
                setWeights={setWeights}
                onSave={onSaveSettings}
                saving={saving}
                saveStatus={saveStatus}
              />
            )}
            {tab === "uploads" && (
              <ManageUploads onUploaded={onUploaded} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(20, 22, 28, 0.45)",
  zIndex: 100,
  display: "flex",
  alignItems: "stretch",
  justifyContent: "center",
  padding: "24px",
};

const shellStyle = {
  background: C.card,
  borderRadius: 10,
  border: `1px solid ${C.border}`,
  width: "100%",
  maxWidth: 1000,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "18px 22px",
  borderBottom: `1px solid ${C.border}`,
  background: C.bg,
};

const closeBtnStyle = {
  background: "none",
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  width: 30,
  height: 30,
  cursor: "pointer",
  color: C.sub,
  fontSize: 14,
};

const bodyStyle = {
  display: "grid",
  gridTemplateColumns: "180px 1fr",
  flex: 1,
  minHeight: 0,
};

const railStyle = {
  background: C.bg,
  borderRight: `1px solid ${C.border}`,
  padding: "12px 0",
  display: "flex",
  flexDirection: "column",
};

const railBtnStyle = {
  textAlign: "left",
  padding: "10px 16px",
  fontSize: 13,
  border: "none",
  cursor: "pointer",
  background: "transparent",
};

const paneStyle = {
  padding: 20,
  overflow: "auto",
};
